/**
 * The recordings an instrument is played on, fetched and made ready, and which one a note
 * is played on.
 *
 * Each instrument's file (`public/soundfonts/<name>.json`, written by `pnpm sounds:fetch`)
 * is `{ layers, plain }`. A layer is the recordings made at one dynamic -- `p` or `f`, or
 * `null` where the instrument was recorded at only one -- keyed by MIDI key. They are
 * VSCO-2's, and most are three or four semitones apart, so a note is played on the nearest
 * one, shifted to its pitch. `plain` is FluidR3's notes, one per semitone, for the keys
 * further than {@link SHIFT} from anything VSCO-2 recorded; they are played whatever the
 * dynamic, since there is only one of each.
 *
 * A string has two files, one of it bowed and one of it plucked (`<name>_pizzicato.json`),
 * and a note the score has plucked is played on the second.
 */

import type { DynamicMark } from "@app/score-editor";
import { hertz, VELOCITIES } from "@app/score-editor";
import { prepare } from "./sampler-prepare";
import type { Prepared } from "./sampler-prepare";
import { instrumentOf, pluckedOf } from "./sampler-seats";
import type { Instrument } from "./sampler-seats";

/**
 * The recordings on disk, by the General MIDI program each one is.
 *
 * Twenty-three of the 128 programs: the orchestra this project scores for, the piano it most
 * often reads from, and the four saxophones, the guitar, the harp, the organ and the celesta
 * an arrangement may be asked for. A part playing anything else is heard on the oscillator, which is the right
 * answer: a wrong instrument is worse than a plain one. A euphonium is written the tuba's
 * program, General MIDI having none for it, and so is heard on the tuba's.
 */
const RECORDED: ReadonlyMap<number, string> = new Map([
  [0, "acoustic_grand_piano"],
  [8, "celesta"],
  [19, "church_organ"],
  [24, "acoustic_guitar_nylon"],
  [40, "violin"],
  [41, "viola"],
  [42, "cello"],
  [43, "contrabass"],
  [46, "orchestral_harp"],
  [56, "trumpet"],
  [57, "trombone"],
  [58, "tuba"],
  [60, "french_horn"],
  [64, "soprano_sax"],
  [65, "alto_sax"],
  [66, "tenor_sax"],
  [67, "baritone_sax"],
  [68, "oboe"],
  [69, "english_horn"],
  [70, "bassoon"],
  [71, "clarinet"],
  [72, "piccolo"],
  [73, "flute"],
]);

/**
 * The four strings plucked, by the program of the instrument: VSCO-2 recorded each of them
 * that way too, on the same keys as it recorded them bowed.
 */
const PLUCKED: ReadonlyMap<number, string> = new Map([
  [40, "violin_pizzicato"],
  [41, "viola_pizzicato"],
  [42, "cello_pizzicato"],
  [43, "contrabass_pizzicato"],
]);

/** What a note is played on: the instrument, and whether a string of it is plucked. */
export interface Sound {
  readonly program: number | null;
  readonly plucked: boolean;
}

/** One file of recordings, and how the instrument recorded in it sits and lets go. */
export interface Recording {
  readonly name: string;
  readonly instrument: Instrument;
}

/**
 * The files a sound may be heard on, the one it asks for first.
 *
 * A plucked string is followed by the same string bowed, which is what it is heard on where
 * a board has not run `pnpm sounds:fetch` since the plucked recordings were added: the right
 * instrument played the wrong way is nearer than an oscillator.
 */
export function recordingsOf({ program, plucked }: Sound): readonly Recording[] {
  if (program === null) return [];
  const instrument = instrumentOf(program);
  const bowed = RECORDED.get(program);
  const pizzicato = plucked ? PLUCKED.get(program) : undefined;
  return [
    ...(pizzicato === undefined ? [] : [{ name: pizzicato, instrument: pluckedOf(instrument) }]),
    ...(bowed === undefined ? [] : [{ name: bowed, instrument }]),
  ];
}

/**
 * Semitones a VSCO-2 recording is shifted at most. `scripts/fetch-soundfonts.mjs` holds the
 * same number and fills every key further than this from a recording with FluidR3's, so
 * every key an instrument has is within it. Past three a recording starts to sound like a
 * different instrument. FluidR3's are never shifted: there is one for every key it has, and
 * a key it has none for is out of range and heard on the oscillator.
 */
const SHIFT = 3;

/**
 * One key's recording, the pitch it sounds at unshifted, how hard it was played, 0 to 1 --
 * 1 for a recording made at one dynamic only, which is played as it always was -- and whether
 * it may be shifted to the keys around it.
 */
interface Zone {
  readonly sample: Prepared;
  readonly root: number;
  readonly nominal: number;
  readonly shifts: boolean;
}

/** The recordings of one dynamic, by the key each sounds. */
interface Layer {
  /** The dynamic it was recorded at, which chooses between layers. */
  readonly nominal: number;
  readonly zones: ReadonlyMap<number, Zone>;
}

/** An instrument's recordings, softest layer first. */
export interface Recorded {
  readonly layers: readonly Layer[];
}

/** A recording chosen for a note: the zone, the shift to its pitch and the layer's level. */
export interface Chosen {
  readonly sample: Prepared;
  /** `playbackRate` that brings the recording to the note's pitch. */
  readonly rate: number;
  /** How hard the recording was played, which its brightness is counted from. */
  readonly nominal: number;
}

interface File {
  readonly layers: readonly {
    readonly dynamic: DynamicMark | null;
    readonly notes: Readonly<Record<string, string>>;
  }[];
  readonly plain: Readonly<Record<string, string>>;
}

/**
 * The layer a note at `velocity` is played on: the one whose dynamic it is nearer, the line
 * between two layers being halfway between them.
 */
function layerFor(recorded: Recorded, velocity: number): Layer | undefined {
  let best: Layer | undefined;
  for (const layer of recorded.layers) {
    if (
      best === undefined ||
      Math.abs(layer.nominal - velocity) < Math.abs(best.nominal - velocity)
    )
      best = layer;
  }
  return best;
}

/** The recording `midi` is played on at `velocity`, or `null` for a key out of range. */
export function choose(recorded: Recorded, midi: number, velocity: number): Chosen | null {
  const layer = layerFor(recorded, velocity);
  if (layer === undefined) return null;
  for (let away = 0; away <= SHIFT; away += 1) {
    // Below first: a recording slowed down is less changed by it than one sped up.
    for (const root of [midi - away, midi + away]) {
      const zone = layer.zones.get(root);
      if (zone === undefined || (away > 0 && !zone.shifts)) continue;
      return { sample: zone.sample, rate: 2 ** ((midi - root) / 12), nominal: zone.nominal };
    }
  }
  return null;
}

/**
 * Whether a decoded sample is a sound at all.
 *
 * FluidR3 has a file for all 88 keys of every instrument, and some of them hold nothing: the
 * tuba's top keys decode to silence. Those are thrown away rather than stored, so a note
 * written there is sounded on the oscillator. It has to be heard: a part that cannot be
 * played is the one thing an arrangement most needs to say out loud, and silence says nothing.
 */
function audible(sound: AudioBuffer): boolean {
  const wave = sound.getChannelData(0);
  for (let i = 0; i < wave.length; i += 1) if (Math.abs(wave[i] ?? 0) > 1e-4) return true;
  return false;
}

/** Decode each note of `notes` into a zone, leaving out the silent ones. */
async function decodeAll(
  context: BaseAudioContext,
  notes: Readonly<Record<string, string>>,
  sustained: boolean,
  made: Pick<Zone, "nominal" | "shifts">,
  into: Map<number, Zone>,
): Promise<void> {
  await Promise.all(
    Object.entries(notes).map(async ([key, uri]) => {
      const root = Number(key);
      if (!Number.isInteger(root)) return;
      // Each note is a `data:` URI, which `fetch` reads without going near the network.
      const bytes = await (await fetch(uri)).arrayBuffer();
      const sound = await context.decodeAudioData(bytes);
      if (!audible(sound)) return;
      into.set(root, { sample: prepare(context, sound, hertz(root), sustained), root, ...made });
    }),
  );
}

/** Whether `value` is a file this version wrote, not the flat one an earlier version did. */
function isFile(value: unknown): value is File {
  if (typeof value !== "object" || value === null) return false;
  const { layers, plain } = value as { layers?: unknown; plain?: unknown };
  return Array.isArray(layers) && typeof plain === "object" && plain !== null;
}

/**
 * Fetch and decode one file of recordings, or `null` where there is no such file or it is
 * not one this version reads -- a board that has not run `pnpm sounds:fetch` since it changed.
 */
export async function readRecorded(
  context: BaseAudioContext,
  { name, instrument }: Recording,
): Promise<Recorded | null> {
  const response = await fetch(`${import.meta.env.BASE_URL}soundfonts/${name}.json`);
  if (!response.ok) return null;
  const file: unknown = await response.json();
  if (!isFile(file)) return null;

  const { sustained } = instrument;
  const plain = new Map<number, Zone>();
  await decodeAll(context, file.plain, sustained, { nominal: 1, shifts: false }, plain);
  const layers = await Promise.all(
    file.layers.map(async ({ dynamic, notes }) => {
      const nominal = dynamic === null ? 1 : VELOCITIES[dynamic];
      const zones = new Map(plain);
      await decodeAll(context, notes, sustained, { nominal, shifts: true }, zones);
      return { nominal, zones };
    }),
  );
  return { layers };
}
